/**
 * Debug Utility
 *
 * Provides wrapped console methods that only output in development or debug mode.
 * This helps keep production builds clean and improves performance by avoiding
 * unnecessary console operations.
 *
 * Usage:
 *   import { debugLog, debugWarn, debugError, debugInfo } from '../utils/debug';
 *   debugLog('User data loaded:', userData);
 *   debugWarn('Deprecated feature used');
 */

// Check if we're in development mode or if DEBUG flag is explicitly set
const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = process.env.REACT_APP_DEBUG === 'true' || isDevelopment;

/**
 * Wrapped console.log that only outputs in development/debug mode
 */
export const debugLog = (...args) => {
  if (isDebugEnabled) {
    console.log(...args);
  }
};

/**
 * Wrapped console.warn that only outputs in development/debug mode
 */
export const debugWarn = (...args) => {
  if (isDebugEnabled) {
    console.warn(...args);
  }
};

/**
 * Wrapped console.error that only outputs in development/debug mode
 * Note: Consider keeping critical errors visible in production
 */
export const debugError = (...args) => {
  if (isDebugEnabled) {
    console.error(...args);
  }
};

/**
 * Wrapped console.info that only outputs in development/debug mode
 */
export const debugInfo = (...args) => {
  if (isDebugEnabled) {
    console.info(...args);
  }
};

/**
 * Wrapped console.table that only outputs in development/debug mode
 */
export const debugTable = (...args) => {
  if (isDebugEnabled) {
    console.table(...args);
  }
};

/**
 * Wrapped console.group that only outputs in development/debug mode
 */
export const debugGroup = (...args) => {
  if (isDebugEnabled) {
    console.group(...args);
  }
};

/**
 * Wrapped console.groupEnd that only outputs in development/debug mode
 */
export const debugGroupEnd = () => {
  if (isDebugEnabled) {
    console.groupEnd();
  }
};

/**
 * Export debug mode status for conditional logic
 */
export const isDebugMode = () => isDebugEnabled;
